import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Target from "../../Icons/Target";
import { Link, useNavigate, useParams } from "react-router-dom";
import {addShooting, deleteShooting, getAthleteById, getEventById, getShootingById, updateShooting} from "../../db";
import {IconArrowLeft, IconArrowRight, IconArrowUp, IconArrowDown, IconPlus, IconTrash, IconCaretUpDownFilled} from "@tabler/icons-react";
import LoadinPage from "../../Components/LoadingPage";
import Hit from "../../Components/Shooting/Hit";
import AthletesModal from "./Modals/Athletes";
import EventsModal from "./Modals/Events";
import RelatedShootings from "../../Components/Shooting/RelatedShootings";


const Detail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const targetRef = useRef(null);

    const [shooting, setShooting] = useState(null);
    const [athlete, setAthlete] = useState(null);
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [targetBox, setTargetBox] = useState(null);
    const [showDeleteZone, setShowDeleteZone] = useState(false);
    const [draggedPointId, setDraggedPointId] = useState(null);
    const [athleteModal, setAthleteModal] = useState(false);
    const [eventModal, setEventModal] = useState(false);

    useEffect(() => {
        fetchShooting();
    }, [id, navigate, t]);

    useEffect(() => {
        const updateTargetBox = () => {
            if (targetRef.current) {
                // 💥 Step 1: Reset styles to natural size first (initial state)
                targetRef.current.style.width = "100%";
                targetRef.current.style.height = "100%";
                const fixSize = () => {
                    const rect = targetRef.current.getBoundingClientRect();
                    const size = Math.min(rect.width, rect.height);
                    if (size < 10) {
                        setTimeout(() => {
                            fixSize()
                        }, 10);
                        return;
                    }

                    targetRef.current.style.width = `${size}px`;
                    targetRef.current.style.height = `${size}px`;
                    // const squareRect = {
                    //     ...rect,
                    //     width: size,
                    //     height: size
                    // };
                    //
                    // setTargetBox(squareRect);
                }

            }
        };

        setTimeout(updateTargetBox, 200);
        window.addEventListener("resize", updateTargetBox);
        return () => window.removeEventListener("resize", updateTargetBox);
    }, []);

    const fetchShooting = async () => {
        const foundShooting = await getShootingById(id);
        setLoading(false);

        if (!foundShooting) {
            navigate("/shooting", { state: { error: t("shootingDetail.notFound") } });
            return;
        }

        setShooting(foundShooting);

        if (foundShooting.athlete) {
            fetchAthlete(foundShooting.athlete);
        } else {
            fetchAthlete(false);
        }
        if (foundShooting.event) {
            const foundEvent = await getEventById(foundShooting.event);
            if (foundEvent) {
                setEvent(foundEvent);
            }
        } else {
            setEvent(false);
        }
    };

    const fetchAthlete = async (athleteId) => {
        const foundAthlete = await getAthleteById(athleteId);
        if (foundAthlete) {
            setAthlete(foundAthlete);
        } else {
            setAthlete(null);
        }
    };

    // ✅ Add a new point when clicking on the target
    const handleAddPoint = async (event) => {
        if (!shooting) return;

        const rect = targetRef.current.getBoundingClientRect();
        console.log(rect);
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;

        const newPoint = {
            id: (shooting.points?.length || 0) + 1,
            x,
            y
        };

        const updatedPoints = [...(shooting.points || []), newPoint];

        const updatedShooting = { ...shooting, points: updatedPoints };
        await updateShooting(shooting.id, { points: updatedPoints });
        setShooting(updatedShooting);
    };

    // ✅ Show delete zone when dragging starts
    const handleDragStart = (pointId) => {
        setShowDeleteZone(true);
        setDraggedPointId(pointId);
    };

    // ✅ Handle drag end and check if it's inside delete zone
    const handleDragEnd = async (pointId, clientX, clientY) => {
        const deleteZone = document.getElementById("delete-area")?.getBoundingClientRect();
        if (!deleteZone) return;

        const rect = targetRef.current.getBoundingClientRect();

        // ✅ Convert delete zone position to percentages relative to the target box
        const deleteLeft = ((deleteZone.left - rect.left) / rect.width) * 100;
        const deleteRight = ((deleteZone.right - rect.left) / rect.width) * 100;
        const deleteTop = ((deleteZone.top - rect.top) / rect.height) * 100;
        const deleteBottom = ((deleteZone.bottom - rect.top) / rect.height) * 100;

        // ✅ Check if the point was dropped inside the delete zone (all in percentages)
        if (
            clientX >= deleteLeft && clientX <= deleteRight &&
            clientY >= deleteTop && clientY <= deleteBottom
        ) {
            await handleDeletePoint(pointId);
        } else {
            // ✅ Otherwise, save the new position
            await handleSavePoint(pointId, clientX, clientY);
        }

        setShowDeleteZone(false);
    };

    // ✅ Handle Moving a Point
    const handleMovePoint = (pointId, clientX, clientY, setCurrentX, setCurrentY) => {
        if (!shooting) return;

        const rect = targetRef.current.getBoundingClientRect();
        const x = ((clientX - rect.left) / rect.width) * 100;
        const y = ((clientY - rect.top) / rect.height) * 100;

        if (x < -20 || x > 120 || y < -20 || y > 120) return;

        setCurrentX(x);
        setCurrentY(y);
    };

    // ✅ Save the new position of a point
    const handleSavePoint = async (pointId, clientX, clientY) => {
        if (!shooting) return;

        let updatedPoints = shooting.points.map((point) =>
            point.id === pointId ? { ...point, x: clientX, y: clientY } : point
        );

        const updatedShooting = { ...shooting, points: updatedPoints };
        await updateShooting(shooting.id, { points: updatedPoints });
        setShooting(updatedShooting);
    };

    // ✅ Handle Deleting a Point
    const handleDeletePoint = async (pointId) => {
        if (!shooting) return;

        let updatedPoints = shooting.points.filter((point) => point.id !== pointId);

        updatedPoints = updatedPoints.map((point, index) => ({
            ...point,
            id: index + 1, // ✅ Recalculate IDs to keep order consistent
        }));

        const updatedShooting = { ...shooting, points: [...updatedPoints] }; // ✅ Force state update with a new array
        await updateShooting(shooting.id, { points: updatedPoints });
        setShooting(updatedShooting); // ✅ Ensures React fully re-renders
    };

    const handleShootingUpdate = async (parameter, value) => {
        if (!shooting) return;

        // ✅ Create updated shooting object
        const updatedShooting = { ...shooting, [parameter]: value };

        // ✅ Save to database
        await updateShooting(shooting.id, { [parameter]: value });

        // ✅ Update state with the new value
        setShooting(updatedShooting);
    };

    const removeShooting = async () => {
        if (window.confirm(t("shootingDetail.removeConfirmation"))) {
            await deleteShooting(shooting.id);
            navigate("/shooting", { state: { error: t("shootingDetail.removed") } });
        }
    }

    const newShooting = async () => {
        try {
            let settings = {};
            if (athlete) {
                settings.athlete = athlete.id;
            }
            if (event) {
                settings.event = event.id;
            }
            if (shooting.type) {
                settings.type = shooting.type;
            }

            const shootingId = await addShooting(settings);
            navigate(`/shooting/${shootingId}`);
        } catch (error) {
            console.error("Error adding shooting:", error);
        }
    };

    const handleCorrectionChange = async (direction, amount) => {
        if (!shooting) return;

        const updatedShooting = {
            ...shooting,
            [direction]: (shooting[direction] || 0) + amount,
        };
        setShooting(updatedShooting);

        updateShooting(shooting.id, {[direction]: updatedShooting[direction]});
    }

    const handleNoteChange = async (text) => {
        if (!shooting) return;

        const updatedShooting = {
            ...shooting,
            note: text,
        };

        await updateShooting(shooting.id, {note: updatedShooting.note});

        setShooting(updatedShooting);
    }

    const handleModalCloseAthlete = () => {
        setAthleteModal(false);

        fetchShooting();
    };
    const handleModalCloseEvents = () => {
        setEventModal(false);

        fetchShooting();
    };


    if (loading) return <LoadinPage />;

    return (
        <div className="p-4 flex flex-col h-full relative">
            <div className="flex items-center w-full">
                <div className="shrink-0 w-10">
                    <Link to="/shooting">
                        <IconArrowLeft className="w-6 h-auto" />
                        <span className="sr-only">{t("shootingDetail.backToList")}</span>
                    </Link>
                </div>
                <h1 className="grow text-2xl uppercase text-center font-black transform -skew-x-12 shrink-0">
                    {t("shootingDetail.headline")}
                </h1>
                <button onClick={removeShooting} className="shrink-0 w-10">
                    <IconTrash className="w-6 h-auto" />
                </button>
            </div>

            <div className="grow relative flex flex-col landscape:flex-row justify-between landscape:overflow-y-auto landscape:flex-nowrap">
                <div className="shrink-0 w-full landscape:w-auto landscape:min-w-[10rem] lg:landscape:min-w-[12rem] xl:landscape:min-w-[14rem] landscape:h-full flex flex-wrap gap-4 items-center justify-between landscape:flex-col landscape:justify-start landscape:items-start landscape:overflow-y-auto landscape:flex-nowrap">
                    <div className="basis-[calc(33%-1.5rem)] sm:basis-auto flex gap-3 order-1">
                        {["p", "s"].map((position) => (
                            <label
                                key={position}
                                htmlFor={`shooting-${position}`}
                                className={`p-1 aspect-square rounded-full cursor-pointer border border-[var(--dark-color)] 
                    ${shooting.position === position ? "bg-[var(--dark-color)] text-[var(--light-color)]" : "bg-transparent"}`}
                            >
                                <input
                                    type="radio"
                                    name="position"
                                    value={position}
                                    checked={shooting.position === position}
                                    onChange={() => handleShootingUpdate("position", position)}
                                    id={`shooting-${position}`}
                                    className="sr-only"
                                />
                                {position === "p" ? <div className="w-6 aspect-square flex items-center justify-center font-bold">{t("shooting.p")}</div> : <div className="w-6 aspect-square flex items-center justify-center font-bold">{t("shooting.s")}</div>}
                            </label>
                        ))}
                    </div>

                    <div className="basis-[calc(50%-0.5rem)] sm:basis-auto sm:grow-1 sm:w-auto order-4  sm:order-2">
                        {athlete && (
                            <div className="text-sm md:text-base font-semibold flex items-center gap-2">
                                <Link to={`/athletes/${athlete.id}/shooting`} className="px-1">
                                    <IconArrowLeft className="w-6 h-auto" />
                                </Link>
                                <button className="flex items-center gap-1" onClick={() => setAthleteModal(true)}>
                                    {athlete.nickname || `${athlete.firstname} ${athlete.lastname}`}
                                    <IconCaretUpDownFilled className="w-4 h-auto" />
                                </button>
                            </div>
                        )}
                        {
                            !athlete && (
                                <div className="text-sm md:text-base font-semibold flex items-center gap-2">
                                    <button className="flex items-center gap-1" onClick={() => setAthleteModal(true)}>
                                        {t("shooting.guest")}
                                        <IconCaretUpDownFilled className="w-4 h-auto" />
                                    </button>
                                </div>
                            )
                        }
                    </div>
                    {athleteModal && (
                        <AthletesModal onClose={() => handleModalCloseAthlete()}
                                         shooting={shooting}
                        />
                    )}

                    <div className="basis-[calc(50%-0.5rem)] sm:basis-auto flex justify-end sm:justify-start sm:grow-1 sm:w-auto order-5 sm:order-3">
                        {event && (
                            <div className="text-sm md:text-base font-semibold flex items-center gap-1">
                                <Link to={`/events/${event.id}`} className="px-1">
                                    <IconArrowLeft className="w-6 h-auto" />
                                </Link>
                                <button className="flex items-center gap-1" onClick={() => setEventModal(true)}>
                                    {event.title}
                                    <IconCaretUpDownFilled className="w-4 h-auto" />
                                </button>
                            </div>
                        )}
                        {
                            !event && (
                                <div className="text-sm md:text-base font-semibold flex items-center gap-2">
                                    <button className="flex items-center gap-1" onClick={() => setEventModal(true)}>
                                        {t("shooting.noEvent")}
                                        <IconCaretUpDownFilled className="w-4 h-auto" />
                                    </button>
                                </div>
                            )
                        }
                    </div>
                    {eventModal && (
                        <EventsModal onClose={() => handleModalCloseEvents()}
                                       shooting={shooting}
                        />
                    )}

                    {shooting.date && (
                        <div className="basis-[calc(33%-1rem)] sm:basis-auto text-center sm:text-left landscape:text-left text-xs landscape:text-sm text-gray-500 order-2 sm:order-4">
                            {shooting.date && (() => {
                                const date = new Date(shooting.date * 1000);
                                const day = String(date.getDate()).padStart(2, "0");
                                const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
                                const year = String(date.getFullYear()).slice(-2); // Last 2 digits of year
                                const hours = String(date.getHours()).padStart(2, "0");
                                const seconds = String(date.getSeconds()).padStart(2, "0");

                                return `${day}.${month}.${year} ${hours}:${seconds}`;
                            })()}
                        </div>
                    )}

                    <select
                        className="basis-[calc(33%-1rem)] sm:basis-auto order-3 sm:order-5 border border-[var(--light-color)] bg-white p-1 text-xs landscape:text-sm outline-none rounded-none"
                        value={shooting.type}
                        onChange={(event) => handleShootingUpdate("type", event.target.value)}
                    >
                        <option value="">-</option>
                        <option value="training">{t("shooting.training")}</option>
                        <option value="zeroing">{t("shooting.zeroing")}</option>
                        <option value="race">{t("shooting.race")}</option>
                    </select>
                </div>
                <div
                    ref={targetRef}
                    className="w-full h-full max-w-[90vmin] max-h-[90vmin] relative cursor-crosshair self-center content-center"
                >
                    <Target className="absolute inset-0 object-contain w-full h-full" />
                    <div className="absolute inset-0 object-contain w-full h-full" onClick={handleAddPoint}></div>
                    {shooting.points?.map((point) => (
                        <Hit
                            key={`${point.id}-${point.x}-${point.y}`}
                            point={point}
                            onMove={handleMovePoint}
                            onSave={handleDragEnd}
                            onDragStart={handleDragStart}
                        />
                    ))}
                    {showDeleteZone && (
                        <div
                            id="delete-area"
                            className="absolute bottom-0 left-0 w-12 h-12 bg-red-600 text-white flex items-center justify-center font-bold rounded-lg shadow-md"
                        >
                            <IconTrash className="w-6 h-auto" />
                        </div>
                    )}
                </div>
                <div className="shrink-0 w-full landscape:w-auto  landscape:min-w-[10rem] lg:landscape:min-w-[12rem] xl:landscape:min-w-[14rem] landscape:h-full pr-14 landscape:pr-0 landscape:pb-14 flex flex-wrap items-start justify-between gap-x-6 gap-y-4 landscape:flex-col landscape:justify-start landscape:overflow-y-auto landscape:flex-nowrap light-scrollbar">
                    <div>TODO: Wind</div>
                    <div className="flex flex-col gap-2 text-sm">
                        <div className="flex">
                            <button
                                onClick={() => handleCorrectionChange("correctionVertical", -1)}
                                className="bg-[var(--light-color)] border border-[var(--light-color)] w-8 aspect-square flex items-center justify-center">
                                <IconArrowDown className="w-6 h-auto" />
                            </button>
                            <div className="border border-[var(--light-color)] flex items-center">
                                <div className="w-6 text-center flex items-center justify-center">
                                    {Math.abs(shooting.correctionVertical || 0)}
                                </div>
                                <div className="w-3">
                                    {shooting.correctionVertical ? (
                                        shooting.correctionVertical > 0 ? (
                                            <IconArrowUp className="w-3 h-auto" />
                                        ) : (
                                            <IconArrowDown className="w-3 h-auto" />
                                        )
                                    ) : ''}

                                </div>
                            </div>
                            <button
                                onClick={() => handleCorrectionChange("correctionVertical", 1)}
                                className="bg-[var(--light-color)] border border-[var(--light-color)] w-8 aspect-square flex items-center justify-center">
                                <IconArrowUp className="w-6 h-auto" />
                            </button>
                        </div>
                        <div className="flex">
                            <button className="bg-[var(--light-color)] border border-[var(--light-color)] w-8 aspect-square flex items-center justify-center"
                                    onClick={() => handleCorrectionChange("correctionHorizontal", -1)}
                            >
                                <IconArrowLeft className="w-6 h-auto" />
                            </button>
                            <div className="border border-[var(--light-color)] flex items-center">
                                <div className="w-6 text-center flex items-center justify-center">
                                    {Math.abs(shooting.correctionHorizontal || 0)}
                                </div>
                                <div className="w-3">
                                    {shooting.correctionHorizontal ? (
                                        shooting.correctionHorizontal > 0 ? (
                                            <IconArrowRight className="w-3 h-auto" />
                                        ) : (
                                            <IconArrowLeft className="w-3 h-auto" />
                                        )
                                    ) : ''}
                                </div>
                            </div>
                            <button
                                onClick={() => handleCorrectionChange("correctionHorizontal", 1)}
                                className="bg-[var(--light-color)] border border-[var(--light-color)] w-8 aspect-square flex items-center justify-center">
                                <IconArrowRight className="w-6 h-auto" />
                            </button>
                        </div>
                    </div>
                    <textarea
                        placeholder={t("shootingDetail.notePlaceholder")}
                        onChange={(event) => {handleNoteChange( event.target.value) }}
                        value={shooting.note || ''}
                        className="border border-[var(--light-color)] p-1 text-xs min-w-24 w-full h-14 max-w-72 outline-none rounded-none landscape:min-h-[4rem]"></textarea>
                    <div className="w-full">
                        <RelatedShootings shooting={shooting} />
                    </div>
                </div>
            </div>
            <button
                onClick={newShooting}
                className="p-4 rounded-full bg-[var(--dark-color)] text-[var(--light-color)] absolute right-2 bottom-2"
            >
                <IconPlus className="w-6 h-auto" />
                <span className="sr-only">{t("athletesShooting.addNew")}</span>
            </button>
        </div>
    );
};

export default Detail;
